import { Autocomplete } from "@mui/material";
import { Axios } from "api/axios";
import BootstrapTextField from "Common/BootstrapTextField";
import CancelButton from "Common/Components/Buttons/CancelButton";
import PrimarySubmitButton from "Common/Components/Buttons/PrimarySubmitButton";
import PhoneInputComponent from "Common/Components/InputComponent/PhoneInput";
import PageBreadcrumb from "Common/Components/PageBreadcrumb/PageBreadcrumb";
import PageContainer from "Common/Components/PageContainer/PageContainer";
import LoadingSpinner from "components/LoadingSpinner";
import { MecordColors } from "Constants/Color";
import { autocompleteDropdownStyles } from "Constants/Style";
import { TableKeys } from "Constants/TableKeys/TableKeys";
import { City, Country, State } from "country-state-city";
import { useFormik } from "formik";
import { clearTableState } from "helpers/ClearTableState/ClearTableState";
import { useScrollToFormikError } from "hooks/useScrollToFormikError";
import { useEffect, useState } from "react";
import { Button, Col, Form, Row, Spinner } from "react-bootstrap";
import { BsEnvelope, BsTelephone, BsTrash } from "react-icons/bs";
import { useDispatch } from "react-redux";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import {
  addCompany,
  fetchCompanyById,
  updateCompany,
} from "slices/Masters/company/thunk";
import * as Yup from "yup";
import { fetchAddressFromPincode } from "helpers/geonamesHelper";


// --- Interfaces ---
interface Contact {
  contact_id?: string;
  contact_name: string;
  contact_phone: string;
  contact_email: string;
  designation?: string;
  // department?: string;
}

interface Address {
  shipping_address_id?: string;
  address: string;
  country: string;
  state: string;
  city: string;
  pincode: string;
  phone_number?: string;
}

interface CompanyFormData {
  company_name: string;
  branch_name: string;
  vendor_code: string;
  industry: string;
  phone_number: string;
  company_email: string;
  gstin: string;
  gst_type: string;
  billing_address: Address;
  shipping_addresses: Address[];
  contacts: Contact[];
}

const initialBillingAddress: Address = {
  address: "",
  country: "",
  state: "",
  city: "",
  pincode: "",
  phone_number: "",
};

const initialShippingAddress: Address = {
  address: "",
  country: "",
  state: "",
  city: "",
  pincode: "",
  phone_number: "",
};

const initialContact: Contact = {
  contact_name: "",
  contact_phone: "",
  contact_email: "",
  designation: "",
  // department: "",
};

// Validation Schema will be created dynamically inside the component

// Strict email validation regex
// Validates proper email format: local-part@domain.tld
// - Local part: allows letters, numbers, dots, underscores, hyphens, plus, percent
// - Domain: only letters, dots, and hyphens (no numbers in domain)
// - TLD: at least 2 letters only
// const emailRegex =
//   /^[a-zA-Z0-9._%+-]+@[a-zA-Z][a-zA-Z.-]*[a-zA-Z]\.[a-zA-Z]{2,}$/;

const emailRegex = 
  /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9][a-zA-Z0-9.-]*[a-zA-Z0-9]\.[a-zA-Z]{2,}$/;
const panRegex = /^[A-Z]{5}[0-9]{4}[A-Z]{1}$/;
const gstRegex = /^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z]{1}[1-9A-Z]{1}Z[0-9A-Z]{1}$/i;
const cinRegex = /^[A-Z]{1}[0-9]{5}[A-Z]{2}[0-9]{4}[A-Z]{3}[0-9]{6}$/i;

const contactSchema = Yup.object().shape({
  contact_name: Yup.string().trim().required("Contact name is required"),
  contact_phone: Yup.string().trim().required("Contact phone is required"),
  contact_email: Yup.string()
    .trim()
    .matches(
      emailRegex,
      "Please enter a valid email address (e.g., example@domain.com)",
    )
    .required("Contact email is required"),
  designation: Yup.string().trim().optional(),
  // department: Yup.string(),
});

const validationSchema = Yup.object().shape({
  company_name: Yup.string().trim().required("Company name is required"),
  branch_name: Yup.string().trim().required("Location name is required"),

  industry: Yup.string().trim().required("Industry is required"),
  // .matches(/^[0-9]{10}$/, "Phone number must be 10 digits")
  // .required("Phone number is required"),
  company_email: Yup.string()
    .trim()
    .matches(
      emailRegex,
      "Please enter a valid email address (e.g., example@domain.com)",
    )
    .required("Company email is required"),
  gstin: Yup.string()
    .trim()
    .when("gst_type", {
      is: "Regular",
      then: (schema) =>
        schema
          .matches(
            gstRegex,
            "Please enter a valid GST number (e.g., 27AAPFU0939F1ZV)",
          )
          .required("GSTIN is required when GST type is Regular"),
      otherwise: (schema) => schema.optional(),
    }),
  gst_type: Yup.string()
    .trim()
    .required("GST type is required")
    .oneOf(["Regular", "Compositional gst"], "Please select a valid GST type"),
  phone_number: Yup.string().trim().optional(),

  billing_address: Yup.object()
    .shape({
      address: Yup.string().trim().required("Address is required"),
      country: Yup.string().trim().required("Country is required"),
      state: Yup.string().trim(),
      city: Yup.string().trim(),
      pincode: Yup.string().trim().required("Pin code is required"),
      phone_number: Yup.string().trim().required("Phone number is required"),
    })
    .required("Billing address is required"),
  shipping_addresses: Yup.array().of(
    Yup.object().shape({
      address: Yup.string().trim().required("Address is required"),
      country: Yup.string().trim().required("Country is required"),
      state: Yup.string().trim(),
      city: Yup.string().trim(),
      pincode: Yup.string().trim().required("Pin code is required"),
      phone_number: Yup.string().trim().required("Phone number is required"),
    }),
  ),
  contacts: Yup.array().of(contactSchema),
});

interface CompanyFormProps {
  isModal?: boolean;
  onSuccess?: () => void;
}

const CompanyForm = (props: CompanyFormProps) => {
  const { isModal = false, onSuccess } = props;
  const dispatch = useDispatch<any>();
  const { id: urlId } = useParams<{ id: string }>();
  // Ignore URL id when in modal mode to avoid picking up quotation ID from URL
  const id = isModal ? undefined : urlId;
  const navigate = useNavigate();
  const location = useLocation();
  const { company_data } = location.state || {};
  const company_id = company_data?.company_id || "";
  const isEdit = Boolean(id);
  const [isLoadingStates, setIsLoadingStates] = useState(false);
  const [isLoadingCities, setIsLoadingCities] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [countries, setCountries] = useState<any[]>([]);
  const [states, setStates] = useState<any[]>([]);
  const [cities, setCities] = useState<any[]>([]);
  const [currentShippingAddress, setCurrentShippingAddress] = useState<Address>(
    initialShippingAddress,
  );
  const [sameAsBilling, setSameAsBilling] = useState(false);
  const [currentContact, setCurrentContact] = useState<Contact>(initialContact);
  const [shippingAddressErrors, setShippingAddressErrors] = useState<
    Partial<Address>
  >({});
  const [contactErrors, setContactErrors] = useState<Partial<Contact>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Validation Schema - Dynamic state validation
  const createAddressSchema = (countryName?: string) => {
    const hasStates = countryName
      ? State.getStatesOfCountry(
          countries.find((c: any) => c.name === countryName)?.isoCode || "",
        ).length > 0
      : false;

    return Yup.object().shape({
      address: Yup.string().trim().required("Address is required"),
      pincode: Yup.string().trim().required("Pin code is required"),
      city: Yup.string().trim(), // Made optional - will be conditionally required
      state: hasStates
        ? Yup.string().trim().required("State is required")
        : Yup.string().trim(),
      country: Yup.string().trim().required("Country is required"),
      phone_number: Yup.string()
        .trim()
        // .matches(/^[0-9]{10}$/, "Phone number must be 10 digits")
        .required("Phone number is required"),
    });
  };

  const commonInputStyle = {
    fontSize: "0.8rem",
    minHeight: 38,
    padding: "0.375rem 0.75rem",
  };
  const commonLabelStyle = {
    fontSize: "0.8rem",
    marginBottom: 6,
    marginTop: 12,
  };

  const formik = useFormik<CompanyFormData>({
    initialValues: {
      company_name: "",
      branch_name: "",
      vendor_code: "",
      industry: "",
      phone_number: "",
      company_email: "",
      gstin: "",
      gst_type: "",
      billing_address: initialBillingAddress,
      shipping_addresses: [],
      contacts: [],
    },
    validationSchema,
    enableReinitialize: true,
    validate: (values) => {
      const errors: any = {};

      // Check billing address state requirement
      if (values.billing_address.country) {
        const selectedCountry = countries.find(
          (c: any) => c.name === values.billing_address.country,
        );
        if (selectedCountry) {
          const countryStates = State.getStatesOfCountry(
            selectedCountry.isoCode,
          );
          if (countryStates.length > 0 && !values.billing_address.state) {
            errors.billing_address = errors.billing_address || {};
            errors.billing_address.state = "State is required";
          }

          // Check billing address city requirement
          if (values.billing_address.state) {
            const selectedState = countryStates.find(
              (s: any) => s.name === values.billing_address.state,
            );
            if (selectedState) {
              const stateCities = City.getCitiesOfState(
                selectedCountry.isoCode,
                selectedState.isoCode,
              );
              if (stateCities.length > 0 && !values.billing_address.city) {
                errors.billing_address = errors.billing_address || {};
                errors.billing_address.city = "City is required";
              }
            }
          }
        }
      }

      // Check shipping addresses state and city requirement
      if (values.shipping_addresses && values?.shipping_addresses?.length > 0) {
        values.shipping_addresses?.forEach((address, index) => {
          if (address.country) {
            const selectedCountry = countries.find(
              (c: any) => c.name === address.country,
            );
            if (selectedCountry) {
              const countryStates = State.getStatesOfCountry(
                selectedCountry.isoCode,
              );
              if (countryStates.length > 0 && !address.state) {
                errors.shipping_addresses = errors.shipping_addresses || [];
                errors.shipping_addresses[index] =
                  errors.shipping_addresses[index] || {};
                errors.shipping_addresses[index].state = "State is required";
              }

              // Check shipping address city requirement
              if (address.state) {
                const selectedState = countryStates.find(
                  (s: any) => s.name === address.state,
                );
                if (selectedState) {
                  const stateCities = City.getCitiesOfState(
                    selectedCountry.isoCode,
                    selectedState.isoCode,
                  );
                  if (stateCities.length > 0 && !address.city) {
                    errors.shipping_addresses = errors.shipping_addresses || [];
                    errors.shipping_addresses[index] =
                      errors.shipping_addresses[index] || {};
                    errors.shipping_addresses[index].city = "City is required";
                  }
                }
              }
            }
          }
        });
      }

      if (Object.keys(errors).length === 0) return {};
      return errors;
    },
    onSubmit: async (values) => {
      setIsSubmitting(true);
      try {
        const mappedBillingAddress = mapAddressToPayload(
          values.billing_address,
        );

        const mappedShippingAddresses =
          values.shipping_addresses.map(mapAddressToPayload);

        const mappedContactDetails = values.contacts.map(mapContactToPayload);

        const payload = {
          company_name: values.company_name.trim(),
          branch_name: values.branch_name.trim(),
          vendor_code: values.vendor_code.trim(),
          industry: values.industry.trim(),
          // phone_number: values.phone_number.trim(),
          company_email: values.company_email.trim(),
          gstin: values.gstin.trim(),
          gst_type: values.gst_type.trim(),
          contacts: mappedContactDetails,
          billing_address: mappedBillingAddress,
          shipping_addresses: mappedShippingAddresses,
          ...(values.phone_number?.trim() && {
            phone_number: values.phone_number.trim(),
          }),
        };

        if (id) {
          await dispatch(updateCompany({ id, ...payload })).unwrap();
          toast.success("Customer updated successfully");
        } else {
          await dispatch(addCompany(payload)).unwrap();
          clearTableState(TableKeys.MASTER.CUSTOMER);
          toast.success("Customer created successfully");
        }

        if (isModal && onSuccess) {
          onSuccess();
        } else {
          navigate("/master/company");
        }
      } catch (error: any) {
        const errorMessage =
          (typeof error === "string" && error) ||
          error?.response?.data?.message ||
          error?.message ||
          "Something went wrong";
        toast.error(errorMessage);
      } finally {
        setIsSubmitting(false);
      }
    },
  });

  useScrollToFormikError(
    formik.errors,
    formik.submitCount,
    formik.isSubmitting,
  );

  const mapAddressToPayload = (addressObject: Address) => ({
    ...(id && addressObject.shipping_address_id
      ? { shipping_address_id: addressObject.shipping_address_id }
      : {}),
    address: addressObject.address.trim(),
    country: addressObject.country.trim(),
    pincode: addressObject.pincode.trim(),
    city: addressObject.city.trim(),
    state: addressObject.state.trim(),
    phone_number: addressObject.phone_number
      ? addressObject.phone_number.trim()
      : "",
  });

  const mapContactToPayload = (contactObject: Contact) => ({
    ...(id && contactObject.contact_id
      ? { contact_id: contactObject.contact_id }
      : {}),
    name: contactObject.contact_name.trim(),
    email: contactObject.contact_email.trim(),
    designation: contactObject.designation
      ? contactObject.designation.trim()
      : "",
    // contact_person_department: contactObject.department,
    phone_number: contactObject.contact_phone.trim(),
  });

  const validateShippingAddress = async (
    address: Address,
  ): Promise<boolean> => {
    try {
      const dynamicSchema = createAddressSchema(address.country);
      await dynamicSchema.validate(address, { abortEarly: false });
      setShippingAddressErrors({});
      return true;
    } catch (error: any) {
      const errors: Partial<Address> = {};
      error.inner?.forEach((err: any) => {
        if (err.path) {
          errors[err.path as keyof Address] = err.message;
        }
      });
      setShippingAddressErrors(errors);
      return false;
    }
  };

  const validateContact = async (contact: Contact): Promise<boolean> => {
    try {
      await contactSchema.validate(contact, { abortEarly: false });
      setContactErrors({});
      return true;
    } catch (error: any) {
      const errors: Partial<Contact> = {};
      error.inner?.forEach((err: any) => {
        if (err.path) {
          errors[err.path as keyof Contact] = err.message;
        }
      });
      setContactErrors(errors);
      return false;
    }
  };

  const handleSameAsBillingChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const checked = e.target.checked;
    setSameAsBilling(checked);
    if (checked) {
      const billing = formik.values.billing_address;
      setCurrentShippingAddress({
        address: billing.address || "",
        pincode: billing.pincode || "",
        city: billing.city || "",
        state: billing.state || "",
        country: billing.country || "",
        phone_number: billing.phone_number || "",
      });

      // Fetch states and cities for the shipping dropdowns if the country/state are set
      const selectedCountry = countries.find((c: any) => c.name === billing.country);
      if (selectedCountry) {
        setIsLoadingStates(true);
        const newStates = State.getStatesOfCountry(selectedCountry.isoCode);
        setStates(newStates);
        setIsLoadingStates(false);

        const selectedState = newStates.find((s: any) => s.name === billing.state);
        if (selectedState) {
          setIsLoadingCities(true);
          const newCities = City.getCitiesOfState(
            selectedCountry.isoCode,
            selectedState.isoCode
          );
          setCities(newCities);
          setIsLoadingCities(false);
        }
      }
    } else {
      setCurrentShippingAddress(initialShippingAddress);
    }
  };

  const handleAddShippingAddress = async () => {
    const trimmedAddress = {
      ...currentShippingAddress,
      address: currentShippingAddress.address.trim(),
      pincode: currentShippingAddress.pincode.trim(),
      city: currentShippingAddress.city.trim(),
      state: currentShippingAddress.state.trim(),
      country: currentShippingAddress.country.trim(),
      phone_number: currentShippingAddress.phone_number?.trim() || "",
    };

    const isValid = await validateShippingAddress(trimmedAddress);
    if (isValid) {
      formik.setFieldValue("shipping_addresses", [
        ...formik.values.shipping_addresses,
        trimmedAddress,
      ]);
      setCurrentShippingAddress(initialShippingAddress);
      setShippingAddressErrors({});
      setSameAsBilling(false);
    } else {
      toast.error("Please fill all required fields for shipping address.");
    }
  };

  const handleRemoveShippingAddress = (index: number) => {
    const newAddresses = formik.values.shipping_addresses.filter(
      (_, i) => i !== index,
    );
    formik.setFieldValue("shipping_addresses", newAddresses);
  };

  const handleAddContact = async () => {
    const trimmedContact = {
      ...currentContact,
      contact_name: currentContact.contact_name.trim(),
      contact_phone: currentContact.contact_phone.trim(),
      contact_email: currentContact.contact_email.trim(),
      designation: currentContact.designation?.trim() || "",
    };

    const isValid = await validateContact(trimmedContact);
    if (isValid) {
      formik.setFieldValue("contacts", [
        ...formik.values.contacts,
        trimmedContact,
      ]);
      setCurrentContact(initialContact);
      setContactErrors({});
    }
  };

  const handleRemoveContact = (index: number) => {
    const newContacts = formik.values.contacts.filter((_, i) => i !== index);
    formik.setFieldValue("contacts", newContacts);
  };

  const handleBillingCountryChange = (selectedCountry: any) => {
    formik.setFieldValue(
      "billing_address.country",
      selectedCountry?.name || "",
    );
    formik.setFieldValue("billing_address.state", "");
    formik.setFieldValue("billing_address.city", "");

    if (selectedCountry) {
      setIsLoadingStates(true);
      const newStates = State.getStatesOfCountry(selectedCountry.isoCode);
      setStates(newStates);
      setCities([]);
      setIsLoadingStates(false);
    }
  };

  const handleBillingStateChange = (selectedState: any) => {
    const selectedCountry = countries.find(
      (c: any) => c.name === formik.values.billing_address.country,
    );

    formik.setFieldValue("billing_address.state", selectedState?.name || "");
    formik.setFieldValue("billing_address.city", "");

    if (selectedCountry && selectedState) {
      setIsLoadingCities(true);
      const newCities = City.getCitiesOfState(
        selectedCountry.isoCode,
        selectedState.isoCode,
      );
      setCities(newCities);
      setIsLoadingCities(false);
    }
  };

  const handleBillingCityChange = (selectedCity: any) => {
    formik.setFieldValue("billing_address.city", selectedCity?.name || "");
  };

  const handleShippingCountryChange = (selectedCountry: any) => {
    setSameAsBilling(false);
    setCurrentShippingAddress((prev) => ({
      ...prev,
      country: selectedCountry?.name || "",
      state: "",
      city: "",
    }));

    if (selectedCountry) {
      setIsLoadingStates(true);
      const newStates = State.getStatesOfCountry(selectedCountry.isoCode);
      setStates(newStates);
      setCities([]);
      setIsLoadingStates(false);
    }
  };

  const handleShippingStateChange = (selectedState: any) => {
    setSameAsBilling(false);
    const selectedCountry = countries.find(
      (c: any) => c.name === currentShippingAddress.country,
    );

    setCurrentShippingAddress((prev) => ({
      ...prev,
      state: selectedState?.name || "",
      city: "",
    }));

    if (selectedCountry && selectedState) {
      setIsLoadingCities(true);
      const newCities = City.getCitiesOfState(
        selectedCountry.isoCode,
        selectedState.isoCode,
      );
      setCities(newCities);
      setIsLoadingCities(false);
    }
  };

  const handleShippingCityChange = (selectedCity: any) => {
    setSameAsBilling(false);
    setCurrentShippingAddress((prev) => ({
      ...prev,
      city: selectedCity?.name || "",
    }));
  };

  useEffect(() => {
    const countryList = Country.getAllCountries();
    setCountries(countryList);
  }, []);

  useEffect(() => {
    // Only fetch company data if:
    // 1. Not in modal mode (to avoid picking up quotation ID from URL)
    // 2. id exists and is not empty
    if (!isModal && id) {
      setIsLoading(true);
      dispatch(fetchCompanyById(id))
        .then((res: any) => {
          const response = res?.payload || {};

          if (Object.keys(response).length > 0) {
            // Transform contacts to match form structure
            const transformedContacts = (response?.data?.contacts || []).map(
              (contact: any) => ({
                contact_id: contact.contact_id || "",
                contact_name: contact.name || "",
                contact_phone: contact.phone_number || "",
                contact_email: contact.email || "",
                designation: contact.designation || "",
              }),
            );

            // Transform shipping addresses to match form structure
            const transformedShippingAddresses = (
              response?.data?.shippingAddresses || []
            ).map((addr: any) => ({
              shipping_address_id: addr.shipping_address_id || "",
              address: addr.address || "",
              pincode: addr.pincode || "",
              city: addr.city || "",
              state: addr.state || "",
              country: addr.country || "",
              phone_number: addr.phone_number || "",
            }));

            const dataToSet: CompanyFormData = {
              company_name: response?.data?.company_name || "",
              branch_name: response?.data?.branch_name || "",
              vendor_code: response?.data?.vendor_code || "",
              industry: response?.data?.industry || "",
              phone_number: response?.data?.phone_number || "",
              company_email: response?.data?.company_email || "",
              gstin: response?.data?.gstin || "",
              gst_type: response?.data?.gst_type
                ? response.data.gst_type.charAt(0).toUpperCase() +
                  response.data.gst_type.slice(1)
                : "",
              billing_address: {
                address: response?.data?.billing_address?.address || "",
                country: response?.data?.billing_address?.country || "",
                state: response?.data?.billing_address?.state || "",
                city: response?.data?.billing_address?.city || "",
                pincode: response?.data?.billing_address?.pincode || "",
                phone_number:
                  response?.data?.billing_address?.phone_number || "",
              },
              shipping_addresses: transformedShippingAddresses,
              contacts: transformedContacts,
            };

            formik.setValues(dataToSet);

            const allCountries = Country.getAllCountries();
            const selectedCountry = allCountries.find(
              (c: any) =>
                c.name?.toLowerCase() ===
                dataToSet.billing_address.country?.toLowerCase(),
            );
            const countryIsoCode = selectedCountry?.isoCode || "";
            const statesOfCountry = State.getStatesOfCountry(countryIsoCode);
            const selectedState = statesOfCountry.find(
              (s) =>
                s.name?.toLowerCase() ===
                dataToSet.billing_address.state?.toLowerCase(),
            );
            const stateIsoCode = selectedState?.isoCode || "";

            const citiesOfState = City.getCitiesOfState(
              countryIsoCode,
              stateIsoCode,
            );
            setCountries(allCountries);
            setStates(statesOfCountry);
            setCities(citiesOfState);
          }
        })
        .catch(() => toast.error("Failed to fetch company details"))
        .finally(() => setIsLoading(false));
    }
    // Note: formik is intentionally not included in dependencies to avoid infinite loops
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, isModal, dispatch]);

  const renderAddressFields = (
    addressType: "billing" | "shipping",
    address: Address,
    onAddressChange: (e: React.ChangeEvent<any>) => void,
    onCountryChange: (selected: any) => void,
    onStateChange: (selected: any) => void,
    onCityChange: (selected: any) => void,
    onPhoneNumberChange: (value: string) => void,
    currentErrors: Partial<Address>,
  ) => {
    const billingErrors = formik.errors.billing_address as any;
    const isBilling = addressType === "billing";
    const errors = isBilling ? billingErrors || {} : currentErrors;
    const namePrefix = isBilling ? "billing_address." : "";

    const handlePincodeBlurLocal = async (pinVal?: string) => {
      const pin = pinVal !== undefined ? pinVal.trim() : address.pincode?.trim();
      if (!pin) return;

      const selectedCountry = countries.find(
        (c: any) => c.name === address.country
      );
      const countryCode = selectedCountry?.isoCode;

      try {
        const data = await fetchAddressFromPincode(pin, countryCode);
        if (data) {
          const resolvedCountry = countries.find(
            (c: any) => c.isoCode === data.countryCode
          ) || Country.getAllCountries().find(
            (c: any) => c.isoCode === data.countryCode
          );

          if (resolvedCountry) {
            const countryStates = State.getStatesOfCountry(resolvedCountry.isoCode);
            setStates(countryStates);

            const resolvedState = countryStates.find(
              (s: any) =>
                s.name?.toLowerCase() === data.adminName1?.toLowerCase() ||
                s.isoCode?.toLowerCase() === data.adminCode1?.toLowerCase()
            );

            if (resolvedState) {
              const stateCities = City.getCitiesOfState(
                resolvedCountry.isoCode,
                resolvedState.isoCode
              );
              setCities(stateCities);
            }

            const updatedFields = {
              country: resolvedCountry.name,
              state: resolvedState ? resolvedState.name : data.adminName1 || "",
              city: data.placeName || "",
            };

            if (isBilling) {
              formik.setFieldValue("billing_address.country", updatedFields.country);
              formik.setFieldTouched("billing_address.country", true);
              formik.setFieldValue("billing_address.state", updatedFields.state);
              formik.setFieldTouched("billing_address.state", true);
              formik.setFieldValue("billing_address.city", updatedFields.city);
              formik.setFieldTouched("billing_address.city", true);
            } else {
              setCurrentShippingAddress((prev) => ({
                ...prev,
                ...updatedFields,
              }));
            }
          }
        }
      } catch (err) {
        console.error("Failed to fetch address details for pincode", err);
      }
    };

    return (
      <Row className="mb-3">
        <Col lg={4}>
          <Form.Label className="fw-semibold" style={commonLabelStyle}>
            Address <span className="text-danger">*</span>
          </Form.Label>
          <Form.Control
            type="text"
            id={`${namePrefix}address`}
            name={`${namePrefix}address`}
            placeholder="Enter Address"
            value={address.address}
            onChange={onAddressChange}
            onBlur={(e) => {
              if (isBilling) {
                formik.setFieldTouched("billing_address.address", true);
              } else {
                onAddressChange({
                  target: { name: "address", value: e.target.value.trim() },
                } as any);
              }
            }}
            isInvalid={
              isBilling
                ? formik.touched.billing_address?.address &&
                  !!formik.errors.billing_address?.address
                : !!currentErrors.address
            }
            style={commonInputStyle}
          />
          <Form.Control.Feedback type="invalid">
            {isBilling
              ? formik.errors.billing_address?.address
              : currentErrors.address}
          </Form.Control.Feedback>
        </Col>
        <Col lg={4}>
          <Form.Label className="fw-semibold" style={commonLabelStyle}>
            Pincode <span className="text-danger">*</span>
          </Form.Label>
          <Form.Control
            type="text"
            id={`${namePrefix}pincode`}
            name={`${namePrefix}pincode`}
            placeholder="Enter Pincode"
            value={address?.pincode}
            onChange={(e) => {
              const val = e.target.value.replace(/\D/g, "");
              onAddressChange({
                target: {
                  name: e.target.name,
                  value: val,
                }
              } as any);
              if (val.length === 6) {
                handlePincodeBlurLocal(val);
              }
            }}
            onBlur={(e) => {
              if (isBilling) {
                formik.setFieldTouched("billing_address.pincode", true);
              } else {
                onAddressChange({
                  target: {
                    name: `${namePrefix}pincode`,
                    value: e.target.value.trim(),
                  },
                } as any);
              }
              handlePincodeBlurLocal();
            }}
            isInvalid={
              isBilling
                ? formik.touched.billing_address?.pincode &&
                  !!formik.errors.billing_address?.pincode
                : !!currentErrors.pincode
            }
            style={commonInputStyle}
          />
          <Form.Control.Feedback type="invalid">
            {isBilling
              ? formik.errors.billing_address?.pincode
              : currentErrors.pincode}
          </Form.Control.Feedback>
        </Col>
        <Col lg={4}>
          <Form.Label className="fw-semibold" style={commonLabelStyle}>
            Country <span className="text-danger">*</span>
          </Form.Label>
          <Autocomplete
            options={countries}
            getOptionLabel={(option) => option.name}
            value={
              countries.find(
                (c: any) =>
                  c.name?.toLowerCase() === address.country?.toLowerCase(),
              ) || null
            }
            onChange={(e, selectedCountry) => onCountryChange(selectedCountry)}
            onBlur={() => {
              if (isBilling) {
                formik.setFieldTouched("billing_address.country", true);
              }
            }}
            ListboxProps={autocompleteDropdownStyles.ListboxProps}
            renderOption={(props, option) =>
              autocompleteDropdownStyles.renderOption(
                props,
                option,
                (opt) => opt.name,
              )
            }
            renderInput={(params) => (
              <BootstrapTextField
                {...params}
                id={`${namePrefix}country`}
                name={`${namePrefix}country`}
                placeholder="Select Country"
                size="small"
                error={
                  isBilling
                    ? formik.touched.billing_address?.country &&
                      !!formik.errors.billing_address?.country
                    : !!currentErrors.country
                }
                helperText={
                  isBilling
                    ? formik.errors.billing_address?.country
                    : currentErrors.country
                }
                fullWidth
                sx={{
                  ...autocompleteDropdownStyles.inputSx,
                }}
              />
            )}
          />
        </Col>
        <Col lg={4}>
          {(() => {
            const selectedCountry = countries.find(
              (c) => c.name === address.country,
            );
            const countryStates = selectedCountry
              ? State.getStatesOfCountry(selectedCountry.isoCode)
              : [];
            const hasStates = countryStates.length > 0;

            return (
              <>
                <Form.Label className="fw-semibold" style={commonLabelStyle}>
                  {hasStates ? "State" : "State / Region"}{" "}
                  {hasStates && <span className="text-danger">*</span>}
                </Form.Label>
                {hasStates ? (
                  <Autocomplete
                    options={states.filter(
                      (s) =>
                        s.countryCode ===
                        countries.find((c) => c.name === address.country)
                          ?.isoCode,
                    )}
                    getOptionLabel={(option) => option.name}
                    loading={isLoadingStates}
                    value={
                      states.find(
                        (s: any) =>
                          s.name?.toLowerCase() ===
                          address.state?.toLowerCase(),
                      ) || null
                    }
                    onChange={(e, selectedState) =>
                      onStateChange(selectedState)
                    }
                    onBlur={() => {
                      if (isBilling) {
                        formik.setFieldTouched("billing_address.state", true);
                      }
                    }}
                    ListboxProps={autocompleteDropdownStyles.ListboxProps}
                    renderOption={(props, option) =>
                      autocompleteDropdownStyles.renderOption(
                        props,
                        option,
                        (opt) => opt.name,
                      )
                    }
                    renderInput={(params) => (
                      <BootstrapTextField
                        {...params}
                        id={`${namePrefix}state`}
                        name={`${namePrefix}state`}
                        placeholder="Select State"
                        size="small"
                        error={
                          isBilling
                            ? formik.touched.billing_address?.state &&
                              !!formik.errors.billing_address?.state
                            : !!currentErrors.state
                        }
                        helperText={
                          isBilling
                            ? formik.errors.billing_address?.state
                            : currentErrors.state
                        }
                        fullWidth
                        sx={{
                          ...autocompleteDropdownStyles.inputSx,
                        }}
                      />
                    )}
                    disabled={!address.country}
                  />
                ) : (
                  <Form.Control
                    type="text"
                    id={`${namePrefix}state`}
                    name={`${namePrefix}state`}
                    placeholder="Enter State / Region"
                    value={address.state}
                    onChange={(e) => {
                      if (isBilling) {
                        formik.setFieldValue(e.target.name, e.target.value);
                        formik.setFieldTouched("billing_address.state", true);
                      } else {
                        onAddressChange(e);
                      }
                    }}
                    onBlur={(e) => {
                      if (isBilling) {
                        formik.setFieldTouched("billing_address.state", true);
                      } else {
                        onAddressChange({
                          target: {
                            name: `${namePrefix}state`,
                            value: e.target.value.trim(),
                          },
                        } as any);
                      }
                    }}
                    isInvalid={
                      isBilling
                        ? formik.touched.billing_address?.state &&
                          !!formik.errors.billing_address?.state
                        : !!currentErrors.state
                    }
                    style={commonInputStyle}
                  />
                )}
                {!hasStates && (
                  <Form.Control.Feedback type="invalid">
                    {isBilling
                      ? formik.errors.billing_address?.state
                      : currentErrors.state}
                  </Form.Control.Feedback>
                )}
              </>
            );
          })()}
        </Col>
        <Col lg={4}>
          {(() => {
            const selectedCountry = countries.find(
              (c) => c.name === address.country,
            );
            const countryStates = selectedCountry
              ? State.getStatesOfCountry(selectedCountry.isoCode)
              : [];
            const selectedState = address.state
              ? countryStates.find((s: any) => s.name === address.state)
              : null;
            const availableCities =
              selectedCountry && selectedState
                ? City.getCitiesOfState(
                    selectedCountry.isoCode,
                    selectedState.isoCode,
                  )
                : [];
            const hasCities = availableCities.length > 0;

            return (
              <>
                <Form.Label className="fw-semibold" style={commonLabelStyle}>
                  City {hasCities && <span className="text-danger">*</span>}
                </Form.Label>
                {hasCities ? (
                  <Autocomplete
                    options={cities.filter(
                      (c) =>
                        c.stateCode ===
                        states.find(
                          (s) =>
                            s.name?.toLowerCase() ===
                            address.state?.toLowerCase(),
                        )?.isoCode,
                    )}
                    getOptionLabel={(option) => option.name}
                    loading={isLoadingCities}
                    value={
                      cities.find(
                        (c: any) =>
                          c.name?.toLowerCase() === address.city?.toLowerCase(),
                      ) || null
                    }
                    onChange={(e, selectedCity) => onCityChange(selectedCity)}
                    onBlur={() => {
                      if (isBilling) {
                        formik.setFieldTouched("billing_address.city", true);
                      }
                    }}
                    ListboxProps={autocompleteDropdownStyles.ListboxProps}
                    renderOption={(props, option) =>
                      autocompleteDropdownStyles.renderOption(
                        props,
                        option,
                        (opt) => opt.name,
                      )
                    }
                    renderInput={(params) => (
                      <BootstrapTextField
                        {...params}
                        id={`${namePrefix}city`}
                        name={`${namePrefix}city`}
                        placeholder="Select City"
                        size="small"
                        error={
                          isBilling
                            ? formik.touched.billing_address?.city &&
                              !!formik.errors.billing_address?.city
                            : !!currentErrors.city
                        }
                        helperText={
                          isBilling
                            ? formik.errors.billing_address?.city
                            : currentErrors.city
                        }
                        fullWidth
                        sx={{
                          ...autocompleteDropdownStyles.inputSx,
                        }}
                      />
                    )}
                    disabled={!address.state}
                  />
                ) : (
                  <Form.Control
                    type="text"
                    id={`${namePrefix}city`}
                    name={`${namePrefix}city`}
                    placeholder="Enter City"
                    value={address.city}
                    onChange={(e) => {
                      if (isBilling) {
                        formik.setFieldValue(e.target.name, e.target.value);
                        formik.setFieldTouched("billing_address.city", true);
                      } else {
                        onAddressChange(e);
                      }
                    }}
                    onBlur={(e) => {
                      if (isBilling) {
                        formik.setFieldTouched("billing_address.city", true);
                      } else {
                        onAddressChange({
                          target: {
                            name: `${namePrefix}city`,
                            value: e.target.value.trim(),
                          },
                        } as any);
                      }
                    }}
                    isInvalid={
                      isBilling
                        ? formik.touched.billing_address?.city &&
                          !!formik.errors.billing_address?.city
                        : !!currentErrors.city
                    }
                    style={commonInputStyle}
                  />
                )}
              </>
            );
          })()}
          {(() => {
            const selectedCountry = countries.find(
              (c) => c.name === address.country,
            );
            const countryStates = selectedCountry
              ? State.getStatesOfCountry(selectedCountry.isoCode)
              : [];
            const selectedState = address.state
              ? countryStates.find((s: any) => s.name === address.state)
              : null;
            const availableCities =
              selectedCountry && selectedState
                ? City.getCitiesOfState(
                    selectedCountry.isoCode,
                    selectedState.isoCode,
                  )
                : [];
            const hasCities = availableCities.length > 0;

            return (
              !hasCities && (
                <Form.Control.Feedback type="invalid">
                  {isBilling
                    ? formik.errors.billing_address?.city
                    : currentErrors.city}
                </Form.Control.Feedback>
              )
            );
          })()}
        </Col>
        <Col lg={4}>
          <PhoneInputComponent
            name={`${namePrefix}phone_number`}
            phoneValue={address?.phone_number || ""}
            onPhoneChange={onPhoneNumberChange}
            onPhoneBlur={() => {
              if (isBilling) {
                formik.setFieldTouched("billing_address.phone_number", true);
              } else {
                onAddressChange({
                  target: {
                    name: `${namePrefix}phone_number`,
                    value: address?.phone_number?.trim() || "",
                  },
                } as any);
              }
            }}
            label="Phone Number"
            placeholder="Enter phone number"
            required={true}
            phoneInvalid={
              isBilling
                ? formik.touched.billing_address?.phone_number &&
                  !!formik.errors.billing_address?.phone_number
                : !!currentErrors.phone_number
            }
            phoneErrorMessage={
              isBilling
                ? formik.errors.billing_address?.phone_number
                : currentErrors.phone_number
            }
            style={commonInputStyle}
          />
        </Col>
      </Row>
    );
  };

  const formContent = (
    <>
      {!isModal && (
        <PageBreadcrumb
          title={isEdit || id ? "Edit Customer" : "Add Customer"}
          titleLink="/master/company"
          breadcrumbs={[
            { label: "Customer", to: "/master/company" },
            {
              label: isEdit || id ? "Edit Customer" : "Add Customer",
              to: "",
            },
          ]}
          backNavigationPath={-1}
        />
      )}

      <hr />

      {isEdit && isLoading ? (
        <LoadingSpinner/>
      ) : (
        <>
          <Form autoComplete="off" onSubmit={formik.handleSubmit} noValidate>
            {/* Company Info */}
            <Row className="mb-1">
              <Col xs={12} md={6}>
                <Form.Label className="fw-semibold" style={commonLabelStyle}>
                  Company Name <span className="text-danger">*</span>
                </Form.Label>
                <Form.Control
                  type="text"
                  id="company_name"
                  name="company_name"
                  value={formik.values.company_name}
                  placeholder="Enter Company Name"
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  isInvalid={
                    formik.touched.company_name && !!formik.errors.company_name
                  }
                  style={commonInputStyle}
                />
                <Form.Control.Feedback type="invalid">
                  {formik.errors.company_name}
                </Form.Control.Feedback>
              </Col>
              <Col xs={12} md={6}>
                <Form.Label className="fw-semibold" style={commonLabelStyle}>
                  Location Name <span className="text-danger">*</span>
                </Form.Label>
                <Form.Control
                  type="text"
                  id="branch_name"
                  name="branch_name"
                  placeholder="Enter Location Name"
                  value={formik.values.branch_name}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  isInvalid={
                    formik.touched.branch_name && !!formik.errors.branch_name
                  }
                  style={commonInputStyle}
                />
                <Form.Control.Feedback type="invalid">
                  {formik.errors.branch_name}
                </Form.Control.Feedback>
              </Col>
              <Col xs={12} md={6}>
                <Form.Label className="fw-semibold" style={commonLabelStyle}>
                  Industry <span className="text-danger">*</span>
                </Form.Label>
                <Form.Control
                  type="text"
                  id="industry"
                  name="industry"
                  placeholder="Enter Industry"
                  value={formik.values.industry}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  isInvalid={
                    formik.touched.industry && !!formik.errors.industry
                  }
                  style={commonInputStyle}
                />
                <Form.Control.Feedback type="invalid">
                  {formik.errors.industry}
                </Form.Control.Feedback>
              </Col>
              <Col xs={12} md={6}>
                <PhoneInputComponent
                  name="phone_number"
                  phoneValue={formik.values.phone_number}
                  onPhoneChange={(value) =>
                    formik.setFieldValue(
                      "phone_number",
                      value.replace(/[^\d+\s()-]/g, ""),
                    )
                  }
                  onPhoneBlur={() =>
                    formik.setFieldTouched("phone_number", true)
                  }
                  label="Phone Number"
                  placeholder="Enter phone number"
                  required={false}
                  phoneInvalid={
                    formik.touched.phone_number && !!formik.errors.phone_number
                  }
                  phoneErrorMessage={formik.errors.phone_number}
                  style={commonInputStyle}
                />
              </Col>
            </Row>

            <Row className="mb-1">
       
              <Col xs={12} md={6}>
                <Form.Label className="fw-semibold" style={commonLabelStyle}>
                  Company Email <span className="text-danger">*</span>
                </Form.Label>
                <Form.Control
                  type="email"
                  id="company_email"
                  name="company_email"
                  placeholder="Enter Company Email"
                  value={formik.values.company_email}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  isInvalid={
                    formik.touched.company_email &&
                    !!formik.errors.company_email
                  }
                  style={commonInputStyle}
                />
                <Form.Control.Feedback type="invalid">
                  {formik.errors.company_email}
                </Form.Control.Feedback>
              </Col>
              <Col xs={12} md={6}>
                <Form.Label className="fw-semibold" style={commonLabelStyle}>
                  GSTIN
                  {formik.values.gst_type === "Regular" && (
                    <span className="text-danger"> *</span>
                  )}
                </Form.Label>
                <Form.Control
                  type="text"
                  id="gstin"
                  name="gstin"
                  placeholder="Enter GSTIN"
                  value={formik.values.gstin}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  isInvalid={formik.touched.gstin && !!formik.errors.gstin}
                  style={commonInputStyle}
                />
                <Form.Control.Feedback type="invalid">
                  {formik.errors.gstin}
                </Form.Control.Feedback>
              </Col>
            </Row>

            <Row className="mb-1">
        
              <Col xs={12} md={6}>
                <Form.Label className="fw-semibold" style={commonLabelStyle}>
                  GST Type <span className="text-danger">*</span>
                </Form.Label>
                <Autocomplete
                  options={["Regular", "Compositional gst"]}
                  value={formik.values.gst_type || null}
                  onChange={(e, selectedValue) => {
                    const value = selectedValue ?? "";
                    formik.setFieldValue("gst_type", value, true);
                    formik.setFieldTouched("gst_type", true);
                  }}
                  onBlur={() => formik.setFieldTouched("gst_type", true)}
                  ListboxProps={autocompleteDropdownStyles.ListboxProps}
                  renderOption={(props, option) =>
                    autocompleteDropdownStyles.renderOption(
                      props,
                      option,
                      (opt) => opt as string,
                    )
                  }
                  renderInput={(params) => (
                    <BootstrapTextField
                      {...params}
                      id="gst_type"
                      name="gst_type"
                      placeholder="Select GST Type"
                      size="small"
                      error={
                        formik.touched.gst_type && !!formik.errors.gst_type
                      }
                      helperText={
                        formik.touched.gst_type && formik.errors.gst_type
                      }
                      fullWidth
                      sx={{
                        ...autocompleteDropdownStyles.inputSx,
                      }}
                    />
                  )}
                />
              </Col>
              <Col xs={12} md={6}>
                <Form.Label className="fw-semibold" style={commonLabelStyle}>
                  Vendor Code 
                </Form.Label>
                <Form.Control
                  type="text"
                  id="vendor_code"
                  name="vendor_code"
                  placeholder="Enter Vendor Code"
                  value={formik.values.vendor_code}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  isInvalid={
                    formik.touched.vendor_code && !!formik.errors.vendor_code
                  }
                  style={commonInputStyle}
                />
                <Form.Control.Feedback type="invalid">
                  {formik.errors.vendor_code}
                </Form.Control.Feedback>
              </Col>
            </Row>

            {/* Billing Address */}
            <Row className="mb-0 mt-4">
              <h5 className="fw-bold">Billing Address</h5>
            </Row>
            {renderAddressFields(
              "billing",
              formik.values.billing_address,
              (e) => {
                formik.setFieldValue(e.target.name, e.target.value);
                // formik.setFieldTouched(
                //   `billing_address.${e.target.name}`,
                //   true
                // );
              },
              handleBillingCountryChange,
              handleBillingStateChange,
              handleBillingCityChange,
              (val) => {
                const value = val.replace(/[^\d+\s()-]/g, "");
                formik.setFieldValue("billing_address.phone_number", value);
              },
              {},
            )}

            {/* Shipping Address */}
            <Row className="mb-0 mt-3 align-items-center">
              <Col className="d-flex align-items-center gap-3">
                <h5 className="fw-bold mb-0">New Shipping Address</h5>
                
              </Col>
            </Row>
            <Form.Check
                  type="checkbox"
                  id="same-as-billing-checkbox"
                  label="Same as Billing Address"
                  checked={sameAsBilling}
                  onChange={handleSameAsBillingChange}
                  style={{ fontSize: "0.85rem", fontWeight: "500", marginTop: "4px" }}
                />
            {renderAddressFields(
              "shipping",
              currentShippingAddress,
              (e) => {
                setSameAsBilling(false);
                setCurrentShippingAddress((prev) => ({
                  ...prev,
                  [e.target.name]: e.target.value,
                }));
                if (shippingAddressErrors[e.target.name as keyof Address]) {
                  setShippingAddressErrors((prev) => {
                    const newErrors = { ...prev };
                    delete newErrors[e.target.name as keyof Address];
                    return newErrors;
                  });
                }
              },
              handleShippingCountryChange,
              handleShippingStateChange,
              handleShippingCityChange,
              (value) => {
                setSameAsBilling(false);
                setCurrentShippingAddress((prev) => ({
                  ...prev,
                  phone_number: value.replace(/[^\d+\s()-]/g, ""),
                }));
                if (shippingAddressErrors.phone_number) {
                  setShippingAddressErrors((prev) => {
                    const newErrors = { ...prev };
                    delete newErrors.phone_number;
                    return newErrors;
                  });
                }
              },
              shippingAddressErrors,
            )}

            <div className="d-flex justify-content-start mb-4">
              <button
                type="button"
                onClick={handleAddShippingAddress}
                style={{
                  backgroundColor: "#ffffff",
                  borderColor: MecordColors?.ThemeBGColor,
                  color: MecordColors?.ThemeBGColor,
                  borderRadius: "4px",
                  padding: "8px 16px",
                  fontSize: "14px",
                  fontWeight: "500",
                  cursor: "pointer",
                  transition: "all 0.2s ease",
                  border: `1px solid ${MecordColors?.ThemeBGColor}`,
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.opacity = "0.9";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.opacity = "1";
                }}
              >
                + Add Shipping Address
              </button>
            </div>

            {formik.values.shipping_addresses.length > 0 && (
              <div className="mt-3 mb-3">
                <h6 className="fw-bold mb-3">Added Shipping Addresses</h6>
                <div
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    gap: "12px",
                  }}
                >
                  {formik.values.shipping_addresses.map((addr, index) => (
                    <div
                      key={index}
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        padding: "12px 16px",
                        backgroundColor: "#f9f9f9",
                        borderRadius: "4px",
                        border: "1px solid #e0e0e0",
                      }}
                    >
                      <div style={{ flex: 1 }}>
                        <p
                          style={{
                            margin: 0,
                            fontSize: "0.875rem",
                            color: "#333",
                            lineHeight: "1.5",
                          }}
                        >
                          <strong>Address {index + 1}</strong>
                        </p>
                        <p
                          style={{
                            margin: 0,
                            fontSize: "0.875rem",
                            color: "#666",
                            lineHeight: "1.5",
                          }}
                        >
                          {addr.address}, {addr.city}, {addr.state},{" "}
                          {addr.country}, {addr.pincode}.{" "}
                          {addr.phone_number && `Phone: ${addr.phone_number}`}
                        </p>
                      </div>
                      <Button
                        variant="link"
                        style={{
                          padding: "8px",
                          color: "#dc3545",
                          textDecoration: "none",
                        }}
                        onClick={() => handleRemoveShippingAddress(index)}
                        type="button"
                      >
                        <BsTrash size={18} />
                      </Button>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Contacts */}
            <Row className="mb-0">
              <h5 className="fw-bold">Basic Contact Details</h5>
            </Row>
            <Row className="mb-3">
              <Col xs={12} md={6}>
                <Form.Label className="fw-semibold" style={commonLabelStyle}>
                  Name <span className="text-danger">*</span>
                </Form.Label>
                <Form.Control
                  type="text"
                  name="contact_name"
                  placeholder="Enter Contact Name"
                  value={currentContact.contact_name}
                  onChange={(e) => {
                    setCurrentContact((prev) => ({
                      ...prev,
                      [e.target.name]: e.target.value,
                    }));
                    if (contactErrors.contact_name) {
                      setContactErrors((prev) => ({
                        ...prev,
                        contact_name: "",
                      }));
                    }
                  }}
                  onBlur={(e) => {
                    setCurrentContact((prev) => ({
                      ...prev,
                      contact_name: e.target.value.trim(),
                    }));
                  }}
                  isInvalid={!!contactErrors.contact_name}
                  style={commonInputStyle}
                />
                <Form.Control.Feedback type="invalid">
                  {contactErrors.contact_name}
                </Form.Control.Feedback>
              </Col>
              <Col xs={12} md={6}>
                <Form.Label className="fw-semibold" style={commonLabelStyle}>
                  Designation
                </Form.Label>
                <Form.Control
                  type="text"
                  name="designation"
                  placeholder="Enter Designation"
                  value={currentContact.designation}
                  onChange={(e) => {
                    setCurrentContact((prev) => ({
                      ...prev,
                      [e.target.name]: e.target.value,
                    }));
                  }}
                  onBlur={(e) => {
                    setCurrentContact((prev) => ({
                      ...prev,
                      designation: e.target.value.trim(),
                    }));
                  }}
                  isInvalid={!!contactErrors.designation}
                  style={commonInputStyle}
                />
                <Form.Control.Feedback type="invalid">
                  {contactErrors.designation}
                </Form.Control.Feedback>
              </Col>
            </Row>

            <Row className="mb-3">
              <Col xs={12} md={6}>
                <PhoneInputComponent
                  phoneValue={currentContact.contact_phone}
                  onPhoneChange={(value) => {
                    setCurrentContact((prev) => ({
                      ...prev,
                      contact_phone: value.replace(/[^\d+\s()-]/g, ""),
                    }));
                    if (contactErrors.contact_phone) {
                      setContactErrors((prev) => ({
                        ...prev,
                        contact_phone: "",
                      }));
                    }
                  }}
                  onPhoneBlur={() => {
                    // PhoneInputComponent might not support passing event to blur easily to trim value inside,
                    // but we can trim the state here if needed.
                    // However, PhoneInput often formats value.
                    // Let's at least ensure we trim the state.
                    setCurrentContact((prev) => ({
                      ...prev,
                      contact_phone: prev.contact_phone.trim(),
                    }));
                  }}
                  label="Phone Number"
                  placeholder="Enter contact phone number"
                  required={true}
                  phoneInvalid={!!contactErrors.contact_phone}
                  phoneErrorMessage={contactErrors.contact_phone}
                  style={commonInputStyle}
                />
              </Col>
              <Col xs={12} md={6}>
                <Form.Label className="fw-semibold" style={commonLabelStyle}>
                  Company Email <span className="text-danger">*</span>
                </Form.Label>
                <Form.Control
                  type="email"
                  name="contact_email"
                  placeholder="Enter Contact Email"
                  value={currentContact.contact_email}
                  onChange={(e) => {
                    setCurrentContact((prev) => ({
                      ...prev,
                      [e.target.name]: e.target.value,
                    }));
                    if (contactErrors.contact_email) {
                      setContactErrors((prev) => ({
                        ...prev,
                        contact_email: "",
                      }));
                    }
                  }}
                  onBlur={(e) => {
                    setCurrentContact((prev) => ({
                      ...prev,
                      contact_email: e.target.value.trim(),
                    }));
                  }}
                  isInvalid={!!contactErrors.contact_email}
                  style={commonInputStyle}
                />
                <Form.Control.Feedback type="invalid">
                  {contactErrors.contact_email}
                </Form.Control.Feedback>
              </Col>
            </Row>

            <div className="d-flex justify-content-start mb-4">
              <button
                type="button"
                onClick={handleAddContact}
                disabled={
                  !currentContact.contact_name ||
                  !currentContact.contact_phone ||
                  !currentContact.contact_email
                }
                style={{
                  backgroundColor: "#ffffff",
                  borderColor: MecordColors?.ThemeBGColor,
                  color: MecordColors?.ThemeBGColor,
                  borderRadius: "4px",
                  padding: "8px 16px",
                  fontSize: "14px",
                  fontWeight: "500",
                  cursor:
                    !currentContact.contact_name ||
                    !currentContact.contact_phone ||
                    !currentContact.contact_email
                      ? "not-allowed"
                      : "pointer",
                  transition: "all 0.2s ease",
                  border: `1px solid ${MecordColors?.ThemeBGColor}`,
                  opacity:
                    !currentContact.contact_name ||
                    !currentContact.contact_phone ||
                    !currentContact.contact_email
                      ? 0.6
                      : 1,
                }}
                onMouseEnter={(e) => {
                  if (
                    currentContact.contact_name &&
                    currentContact.contact_phone &&
                    currentContact.contact_email
                  ) {
                    e.currentTarget.style.opacity = "0.9";
                  }
                }}
                onMouseLeave={(e) => {
                  if (
                    currentContact.contact_name &&
                    currentContact.contact_phone &&
                    currentContact.contact_email
                  ) {
                    e.currentTarget.style.opacity = "1";
                  }
                }}
              >
                + Add Contact Details
              </button>
            </div>

            {formik.values.contacts.length > 0 && (
              <div className="mt-4">
                <h6 className="fw-bold mb-3">Added Contact Details</h6>
                <div
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    gap: "12px",
                  }}
                >
                  {formik.values.contacts.map((contact, index) => (
                    <div
                      key={index}
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        padding: "12px 16px",
                        backgroundColor: "#f9f9f9",
                        borderRadius: "4px",
                        border: "1px solid #e0e0e0",
                      }}
                    >
                      <div style={{ flex: 1 }}>
                        <p
                          style={{
                            margin: 0,
                            fontSize: "0.875rem",
                            color: "#333",
                            fontWeight: "600",
                            marginBottom: "4px",
                          }}
                        >
                          <strong>Contact {index + 1}</strong>
                        </p>
                        <p
                          style={{
                            margin: 0,
                            fontSize: "0.875rem",
                            color: "#333",
                            marginBottom: "4px",
                          }}
                        >
                          {contact.contact_name}
                          {contact.designation && (
                            <span style={{ color: "#666", marginLeft: "8px" }}>
                              {contact.designation}
                            </span>
                          )}
                        </p>
                        <div
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: "4px",
                            marginBottom: "4px",
                          }}
                        >
                          <BsTelephone size={14} style={{ color: "#666" }} />
                          <span style={{ fontSize: "0.875rem", color: "#666" }}>
                            {contact.contact_phone}
                          </span>
                        </div>
                        <div
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: "4px",
                          }}
                        >
                          <BsEnvelope size={14} style={{ color: "#666" }} />
                          <span style={{ fontSize: "0.875rem", color: "#666" }}>
                            {contact.contact_email}
                          </span>
                        </div>
                      </div>
                      <Button
                        variant="link"
                        style={{
                          padding: "8px",
                          color: "#dc3545",
                          textDecoration: "none",
                        }}
                        onClick={() => handleRemoveContact(index)}
                        type="button"
                      >
                        <BsTrash size={18} />
                      </Button>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Footer Buttons */}
            <div className="d-flex flex-column flex-md-row justify-content-center gap-3 mt-3">
              {isModal ? (
                <CancelButton
                  onClick={onSuccess}
                  disabled={isLoading}
                  showConfirmation={false}
                />
              ) : (
                <CancelButton
                  onClick={() => navigate("/master/company")}
                  disabled={isLoading}
                  showConfirmation={true}
                />
              )}
              <PrimarySubmitButton
                label={isEdit || id ? "Update" : "Submit"}
                isLoading={isSubmitting || isLoading}
                loadingLabel={isEdit || id ? "Updating..." : "Submitting..."}
              />
            </div>
          </Form>
        </>
      )}
    </>
  );

  if (isModal) {
    return formContent;
  }

  return <PageContainer>{formContent}</PageContainer>;
};

export default CompanyForm;
